// La langue dans laquelle un avis IA est rédigé (issue #168).
//
// Un verdict porte sa langue (`result.lang`) depuis le 2026-10-11, et la nuit
// réécrit ceux qui ne sont pas dans la langue du compte. Les plus anciens n'ont
// pas de langue notée, et la nuit ne les réécrit jamais (`retraduireScores` :
// la deviner pour payer un nouvel appel coûterait plus qu'elle ne rapporte).
// L'écran, lui, peut la deviner gratuitement pour la DIRE, au lieu d'afficher
// un avis français au milieu d'une interface anglaise sans un mot.
//
// Devinée sur les mots outils, jamais sur les mots du métier : « Spring Boot »
// ou « CI/CD » s'écrivent pareil dans les deux langues. Dans le doute, `null`,
// et l'écran se tait.

const FR = new Set(['le', 'la', 'les', 'des', 'du', 'et', 'une', 'un', 'pour', 'avec', 'sans', 'est', 'sont', 'pas', 'au', 'aux', 'dans', 'sur', 'ans', 'non', 'ou', 'mais', 'peu', 'très', 'plus'])
const NL = new Set(['de', 'het', 'een', 'en', 'van', 'met', 'zonder', 'is', 'zijn', 'niet', 'voor', 'op', 'jaar', 'geen', 'of', 'maar', 'weinig', 'zeer', 'meer', 'sterk', 'beperkt', 'ervaring'])
const IT = new Set(['il', 'lo', 'gli', 'della', 'delle', 'del', 'e', 'con', 'senza', 'sono', 'non', 'per', 'su', 'anni', 'nessun', 'nessuna', 'ma', 'poco', 'molto', 'più', 'esperienza', 'forte', 'limitata'])
const ES = new Set(['el', 'los', 'las', 'del', 'y', 'con', 'sin', 'son', 'no', 'para', 'en', 'años', 'ningún', 'ninguna', 'pero', 'poco', 'muy', 'más', 'experiencia', 'fuerte', 'limitada'])
const EN = new Set(['the', 'and', 'of', 'with', 'without', 'is', 'are', 'not', 'for', 'on', 'years', 'no', 'or', 'to', 'an', 'but', 'little', 'very', 'more', 'strong', 'limited'])

import { estLangueUi, type UiLang } from '@ai-job-fit/engine/langues'

interface TexteDeVerdict {
  lang?: string
  dimensions?: { rationale?: string }[]
  majorGaps?: string[]
  dealBreakers?: string[]
}

/** La langue de l'avis : celle qu'il porte, sinon devinée, sinon `null`. */
export function langueVerdict(r: TexteDeVerdict): UiLang | null {
  if (estLangueUi(r.lang)) return r.lang
  const texte = [...(r.dimensions ?? []).map((d) => d.rationale ?? ''), ...(r.majorGaps ?? []), ...(r.dealBreakers ?? [])].join(' ')
  const mots = texte.toLowerCase().split(/[^a-zà-ÿ']+/)
  let fr = 0
  let en = 0
  let nl = 0
  let it = 0
  let es = 0
  for (const m of mots) {
    if (ES.has(m)) es++
    if (FR.has(m)) fr++
    if (EN.has(m)) en++
    if (NL.has(m)) nl++
    if (IT.has(m)) it++
  }
  if (/[éèêç]/.test(texte)) fr += 2
  if (fr >= 3 && fr >= 2 * Math.max(en, nl, it, es)) return 'fr'
  if (en >= 3 && en >= 2 * Math.max(fr, nl, it, es)) return 'en'
  if (nl >= 3 && nl >= 2 * Math.max(fr, en, it, es)) return 'nl'
  if (it >= 3 && it >= 2 * Math.max(fr, en, nl, es)) return 'it'
  if (es >= 3 && es >= 2 * Math.max(fr, en, nl, it)) return 'es'
  return null
}
